/**
 * Reusable GitHub API integration.
 * Used by Chrome Extension releases and can be used elsewhere (repos, files, etc.).
 *
 * Env:
 * - VITE_GITHUB_TOKEN (optional): Personal access token for higher rate limit / private repos.
 */

const GITHUB_API_BASE = 'https://api.github.com'

export interface GitHubReleaseAsset {
  name: string
  browser_download_url: string
  content_type?: string
  size?: number
}

export interface GitHubRelease {
  tag_name: string
  name?: string
  body?: string
  published_at?: string
  assets?: GitHubReleaseAsset[]
  zipball_url?: string
  tarball_url?: string
}

export interface NormalizedRelease {
  version: string
  tagName: string
  downloadUrl: string | null
  publishedAt: string | null
  /** First .zip asset URL, if any */
  zipDownloadUrl: string | null
}

function getToken(): string | undefined {
  return import.meta.env.VITE_GITHUB_TOKEN
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/vnd.github.v3+json'
  }
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

export function getGitHubAuthHeaders(): Record<string, string> {
  return getHeaders()
}

/**
 * Generic GET request to GitHub API.
 * @param path - API path without base (e.g. "/repos/owner/repo/releases")
 */
export async function githubRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const url = path.startsWith('http') ? path : `${GITHUB_API_BASE}${path}`
  const mergedHeaders = {
    ...getHeaders(),
    ...(options?.headers as Record<string, string>)
  }
  let res: Response
  try {
    res = await fetch(url, {
    ...options,
    headers: mergedHeaders
  })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error'
    throw new Error(`Failed to reach GitHub: ${message}. Check your connection and CORS if running in browser.`)
  }
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error(
        'Repo not found (404). The repo may not exist, may be private (set VITE_GITHUB_TOKEN in .env), or owner/repo may be misspelled.'
      )
    }
    if (res.status === 401) {
      throw new Error('GitHub authentication failed. Check VITE_GITHUB_TOKEN in .env if using a token.')
    }
    if (res.status === 403) {
      throw new Error('GitHub API rate limit or access denied. Try again later or set VITE_GITHUB_TOKEN in .env.')
    }
    throw new Error(`GitHub API error: ${res.status}`)
  }
  return res.json()
}

/**
 * List releases for a repository.
 * @param owner - GitHub org or user
 * @param repo - Repository name
 * @param perPage - Max number of releases (default 10)
 */
export async function listReleases(
  owner: string,
  repo: string,
  { perPage = 10 }: { perPage?: number } = {}
): Promise<GitHubRelease[]> {
  const path = `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/releases?per_page=${Math.min(perPage, 100)}`
  const data = await githubRequest<GitHubRelease[]>(path)
  return Array.isArray(data) ? data : []
}

/**
 * Normalize GitHub releases to a common shape (version, tagName, one zip per release).
 * Prefers a .zip asset; if none, uses GitHub's zipball_url (source zip for the tag).
 * Never uses non-zip assets (e.g. .exe, .tar.gz) so each release has exactly one zip download.
 */
export function normalizeReleases(releases: GitHubRelease[]): NormalizedRelease[] {
  return releases.map((r, i) => {
    const tagName = r.tag_name || `release-${i}`
    const version = tagName.replace(/^v/, '') || tagName
    const zipAsset = r.assets?.find((a) => a.name.endsWith('.zip'))
    const zipUrl = zipAsset?.browser_download_url ?? r.zipball_url ?? null
    return {
      version,
      tagName,
      downloadUrl: zipUrl,
      zipDownloadUrl: zipAsset?.browser_download_url ?? null,
      publishedAt: r.published_at ?? null
    }
  })
}

/**
 * Parse "owner/repo" string. Returns null if invalid.
 */
export function parseRepoSlug(slug: string): { owner: string; repo: string } | null {
  const parts = slug.split('/').filter(Boolean)
  if (parts.length < 2) return null
  return { owner: parts[0], repo: parts[1] }
}
